import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

const API_BASE = process.env.REACT_APP_API_BASE;

const WorkerProfileModal = ({ worker, onClose, onApprove }) => {
  const [rejectionReasons, setRejectionReasons] = useState({});
  const [showRejectionBox, setShowRejectionBox] = useState(false);
  const [finalRejectionReason, setFinalRejectionReason] = useState("");
  const [loading, setLoading] = useState(false);

  if (!worker) return null;
  const isFinalized = worker.status === "approved" || worker.status === "rejected";

  const fullUrl = (url) => {
    if (!url) return "#";
    // ✅ Ensure it points to your actual API host
    const apiHost = "https://api.bookyourneed.com";
    return url.startsWith("http") ? url : `${apiHost}${url}`;
  };

  const handleReasonChange = (field, value) => {
    setRejectionReasons((prev) => ({ ...prev, [field]: value }));
  };

  const updateDocStatus = async (field, value) => {
    if (value === "rejected" && !rejectionReasons[field]) {
      toast.error("Enter a reason before rejecting.");
      return;
    }

    try {
      setLoading(true);
      const payload =
        value === "rejected"
          ? { field, value, reason: rejectionReasons[field] }
          : { field, value };

      await axios.post(`${API_BASE}/admin/update-doc-status/${worker._id}`, payload);
      toast.success(`✅ ${field} marked as ${value}`);
      onClose();
    } catch (err) {
      console.error(`❌ Failed to update ${field}`, err);
      toast.error(`Failed to update ${field}.`);
    } finally {
      setLoading(false);
    }
  };

  const updateCertStatus = async (serviceName, status) => {
    if (status === "rejected" && !rejectionReasons[serviceName]) {
      toast.error("Enter a reason before rejecting this certification.");
      return;
    }

    try {
      setLoading(true);
      const payload =
        status === "rejected"
          ? {
              workerId: worker._id,
              serviceName,
              status,
              reason: rejectionReasons[serviceName],
            }
          : { workerId: worker._id, serviceName, status };

      await axios.post(`${API_BASE}/admin/approve-cert`, payload);
      toast.success(`📄 ${serviceName} certification marked as ${status}`);
      onClose();
    } catch (err) {
      console.error("❌ Cert status update failed", err);
      toast.error("Failed to update certificate status.");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectWorker = async () => {
    if (!finalRejectionReason.trim()) {
      toast.error("Please enter a reason before rejecting the worker.");
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_BASE}/admin/reject-worker/${worker._id}`, {
        reason: finalRejectionReason,
      });
      toast.success("❌ Worker rejected and email sent.");
      onClose();
    } catch (err) {
      console.error("❌ Error rejecting worker:", err);
      toast.error("Failed to reject worker.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetStatus = async () => {
    try {
      setLoading(true);
      await axios.post(`${API_BASE}/admin/reset-worker/${worker._id}`);
      toast.success("🔄 Worker reset to pending.");
      onClose();
    } catch (err) {
      console.error("❌ Failed to reset worker:", err);
      toast.error("Error resetting worker.");
    } finally {
      setLoading(false);
    }
  };

  const renderStatus = (status) => {
    if (status === "approved")
      return <span className="text-green-600 font-semibold">Approved ✅</span>;
    if (status === "pending")
      return <span className="text-yellow-600 font-semibold">Pending ⏳</span>;
    if (status === "rejected")
      return <span className="text-red-600 font-semibold">Rejected ❌</span>;
    return <span className="text-gray-600">N/A</span>;
  };

  const renderRejectionReasonInput = (field) => (
    <textarea
      rows={2}
      className="w-full border mt-2 p-2 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
      placeholder="Enter rejection reason..."
      onChange={(e) => handleReasonChange(field, e.target.value)}
    />
  );

  const renderDocumentSection = (label, url, statusField, status) => (
    <div className="border p-4 rounded-lg mb-4 bg-gray-50">
      <h3 className="font-semibold text-lg mb-2">{label}</h3>
      <a
        href={fullUrl(url)}
        target="_blank"
        rel="noreferrer"
        className="text-blue-600 underline"
      >
        View Document
      </a>
      <p>Status: {renderStatus(status)}</p>
      <div className="mt-2 flex gap-3">
        <button
          onClick={() => updateDocStatus(statusField, "approved")}
          className="text-green-600 hover:underline"
        >
          ✅ Approve
        </button>
        <button
          onClick={() => updateDocStatus(statusField, "rejected")}
          className="text-red-600 hover:underline"
        >
          ❌ Reject
        </button>
      </div>
      {status !== "approved" && renderRejectionReasonInput(statusField)}
    </div>
  );

  // ============================================================
  // 🖥️ RENDER MODAL
  // ============================================================
  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white w-full max-w-4xl rounded-2xl p-6 overflow-y-auto max-h-[90vh] shadow-2xl font-[Poppins]"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">👷 Worker Profile</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-red-600 text-lg font-semibold"
            >
              ✕
            </button>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-6">
            <p><strong>Name:</strong> {worker.name}</p>
            <p><strong>Email:</strong> {worker.email}</p>
            <p><strong>Phone:</strong> {worker.phone}</p>
            <p><strong>City:</strong> {worker.city}</p>
            <p><strong>Status:</strong> {worker.status}</p>
            <p><strong>Tier:</strong> {worker.tier}</p>
          </div>

{/* 🪪 ID Verification */}
<div className="border p-4 rounded-lg mb-4 bg-gray-50">
  <h3 className="font-semibold text-lg mb-2">🪪 ID Verification</h3>

  {/* ID 1 */}
  <div className="mb-3">
    <h4 className="font-semibold">{worker.id1Type || "Primary ID"}</h4>
    <div className="flex flex-col gap-1">
      {worker.id1FrontUrl && (
        <p>
          Front:{" "}
          <a
            href={fullUrl(worker.id1FrontUrl)}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            View Front
          </a>
        </p>
      )}
      {worker.id1BackUrl && (
        <p>
          Back:{" "}
          <a
            href={fullUrl(worker.id1BackUrl)}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            View Back
          </a>
        </p>
      )}
    </div>
  </div>

  {/* ID 2 */}
  <div className="mb-3">
    <h4 className="font-semibold">{worker.id2Type || "Secondary ID"}</h4>
    <div className="flex flex-col gap-1">
      {worker.id2FrontUrl && (
        <p>
          Front:{" "}
          <a
            href={fullUrl(worker.id2FrontUrl)}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            View Front
          </a>
        </p>
      )}
      {worker.id2BackUrl && (
        <p>
          Back:{" "}
          <a
            href={fullUrl(worker.id2BackUrl)}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            View Back
          </a>
        </p>
      )}
    </div>
  </div>

  {/* Optional selfie */}
  {worker.selfieUrl && (
    <p>
      Selfie:{" "}
      <a
        href={fullUrl(worker.selfieUrl)}
        target="_blank"
        rel="noreferrer"
        className="text-blue-600 underline hover:text-blue-800"
      >
        View
      </a>
    </p>
  )}

  <p>Status: {renderStatus(worker.backgroundCheckStatus)}</p>
  <div className="mt-2 flex gap-3">
    <button
      onClick={() => updateDocStatus("backgroundCheckStatus", "approved")}
      className="text-green-600 hover:underline"
    >
      ✅ Approve
    </button>
    <button
      onClick={() => updateDocStatus("backgroundCheckStatus", "rejected")}
      className="text-red-600 hover:underline"
    >
      ❌ Reject
    </button>
  </div>
  {worker.backgroundCheckStatus !== "approved" &&
    renderRejectionReasonInput("backgroundCheckStatus")}
</div>
{/* 🔻 Rejection Section */}
{worker.status !== "rejected" && (
  <div className="border p-4 rounded-lg mt-6 bg-red-50">
    <h3 className="font-semibold text-lg mb-2 text-red-700">Reject Application</h3>
    <p className="text-sm text-gray-600 mb-3">
      Please specify your reason for rejection before submitting.
    </p>

    <textarea
      placeholder="Enter rejection reason..."
      value={finalRejectionReason}
      onChange={(e) => setFinalRejectionReason(e.target.value)}
      className="w-full p-3 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-red-400"
      rows={3}
    />

    <div className="flex gap-3">
      <button
        onClick={async () => {
          if (!finalRejectionReason.trim()) {
            toast.error("Please enter a reason before rejecting.");
            return;
          }
          await updateDocStatus("rejected");
          onClose();
        }}
        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
        disabled={loading}
      >
        {loading ? "Rejecting..." : "Reject"}
      </button>

      <button
        onClick={() => setFinalRejectionReason("")}
        className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded-lg"
      >
        Clear
      </button>
    </div>
  </div>
)}


{/* 🛂 Permit (for international workers) */}
{worker.isInternational && worker.permitUrl && (
  <div className="border p-4 rounded-lg mb-4 bg-gray-50">
    <h3 className="font-semibold text-lg mb-2">🛂 Permit</h3>
    <a
      href={fullUrl(worker.permitUrl)}
      target="_blank"
      rel="noreferrer"
      className="text-blue-600 underline hover:text-blue-800"
    >
      View Permit
    </a>
    <p>Status: {renderStatus(worker.permitStatus)}</p>
    <div className="mt-2 flex gap-3">
      <button
        onClick={() => updateDocStatus("permitStatus", "approved")}
        className="text-green-600 hover:underline"
      >
        ✅ Approve
      </button>
      <button
        onClick={() => updateDocStatus("permitStatus", "rejected")}
        className="text-red-600 hover:underline"
      >
        ❌ Reject
      </button>
    </div>
    {worker.permitStatus !== "approved" &&
      renderRejectionReasonInput("permitStatus")}
  </div>
)}

{/* 📷 Profile Photo */}
{worker.profilePhotoUrl && (
  <div className="border p-4 rounded-lg mb-4 bg-gray-50">
    <h3 className="font-semibold text-lg mb-2">📷 Profile Photo</h3>
    <a
      href={fullUrl(worker.profilePhotoUrl)}
      target="_blank"
      rel="noreferrer"
      className="text-blue-600 underline hover:text-blue-800"
    >
      View Photo
    </a>
    <p>Status: {renderStatus(worker.profilePhotoStatus)}</p>
    <div className="mt-2 flex gap-3">
      <button
        onClick={() => updateDocStatus("profilePhotoStatus", "approved")}
        className="text-green-600 hover:underline"
      >
        ✅ Approve
      </button>
      <button
        onClick={() => updateDocStatus("profilePhotoStatus", "rejected")}
        className="text-red-600 hover:underline"
      >
        ❌ Reject
      </button>
    </div>
    {worker.profilePhotoStatus !== "approved" &&
      renderRejectionReasonInput("profilePhotoStatus")}
  </div>
)}

          {/* 📄 Certifications */}
{worker.services?.length > 0 && (
  <div className="border p-4 rounded-lg mb-4 bg-gray-50">
    <h3 className="font-semibold text-lg mb-2">📄 Service Certifications</h3>

    {worker.services.map((service, idx) => {
      const certUrl = service.certUrl;
      const status = service.certStatus || "pending";

      return (
        <div key={idx} className="mb-4 border-b border-gray-200 pb-3">
          <p className="text-gray-800">
            <strong>{service.name}</strong> — Status: {renderStatus(status)}
          </p>

          {certUrl ? (
            <a
              href={fullUrl(certUrl)}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline hover:text-blue-800"
            >
              View Certificate
            </a>
          ) : (
            <p className="text-gray-500 italic">No certificate uploaded.</p>
          )}

          <div className="mt-2 flex flex-wrap gap-3">
            <button
              onClick={() => updateCertStatus(service.name, "approved")}
              disabled={loading}
              className="text-green-600 hover:underline disabled:opacity-50"
            >
              ✅ Approve
            </button>
            <button
              onClick={() => updateCertStatus(service.name, "rejected")}
              disabled={loading}
              className="text-red-600 hover:underline disabled:opacity-50"
            >
              ❌ Reject
            </button>
          </div>

          {/* 📝 Rejection Reason Input */}
          {status !== "approved" &&
            renderRejectionReasonInput(service.name)}
        </div>
      );
    })}
  </div>
)}

          {/* Final Actions */}
          {!isFinalized && (
            <div className="mt-6 flex justify-end gap-4">
              <button
                onClick={() => {
                  onApprove();
                  toast.success("✅ Worker approved.");
                  onClose();
                }}
                className="bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 font-semibold"
              >
                ✅ Approve Worker
              </button>
              {!showRejectionBox ? (
                <button
                  onClick={() => setShowRejectionBox(true)}
                  className="bg-red-600 text-white px-5 py-2 rounded-lg hover:bg-red-700 font-semibold"
                >
                  ❌ Reject Worker
                </button>
              ) : (
                <div className="flex flex-col w-full max-w-md gap-2">
                  <textarea
                    className="border p-2 rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="Enter reason for rejection"
                    value={finalRejectionReason}
                    onChange={(e) => setFinalRejectionReason(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleRejectWorker}
                      className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 font-semibold"
                    >
                      🚫 Send Rejection
                    </button>
                    <button
                      onClick={() => {
                        setFinalRejectionReason("");
                        setShowRejectionBox(false);
                      }}
                      className="bg-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400 font-semibold"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {isFinalized && (
            <div className="mt-6 text-right">
              <button
                onClick={handleResetStatus}
                className="bg-yellow-500 text-white px-5 py-2 rounded-lg hover:bg-yellow-600 font-semibold"
              >
                🔄 Reset to Pending
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WorkerProfileModal;
